package com.flowyride.core.domain.usecase

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RouteEngineId
import com.flowyride.core.domain.model.RoutePreferences
import com.flowyride.core.domain.model.RouteRequest
import com.flowyride.core.domain.routing.RouteEngineRegistry
import com.flowyride.core.domain.routing.RoutingException
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.speedcamera.SpeedCameraRepository
import com.flowyride.core.domain.speedcamera.alongTrack
import com.flowyride.core.domain.util.suspendRunCatching
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/** A computed route and the fixed speed cameras standing on it. */
data class PlannedRoute(
    val route: Route,
    val speedCamerasOnRoute: List<SpeedCamera>,
)

/**
 * @param computationDispatcher Where the CPU-heavy camera matching runs (tests pass their own).
 */
class ComputeRouteUseCase(
    private val registry: RouteEngineRegistry,
    private val speedCameras: SpeedCameraRepository,
    private val computationDispatcher: CoroutineDispatcher = Dispatchers.Default,
) {

    /** @param waypoints Departure, optional intermediate stops, arrival — in riding order. */
    suspend operator fun invoke(
        engineId: RouteEngineId,
        waypoints: List<GeoPoint>,
        preferences: RoutePreferences,
    ): Result<PlannedRoute> = suspendRunCatching {
        val engine = registry.get(engineId)
        if (!engine.isAvailable) throw RoutingException.EngineUnavailable("${engine.displayName} is not configured")
        if (waypoints.size > engine.maxWaypoints) throw RoutingException.TooManyWaypoints(engine.maxWaypoints)

        val route = engine.computeRoute(RouteRequest(waypoints, preferences))
        if (route.track.size < 2) throw RoutingException.NoRouteFound()
        analyze(route)
    }

    /** Everything the planner shows about a route computed elsewhere (e.g. a generated loop). */
    suspend fun analyze(route: Route): PlannedRoute {
        // Cameras are a bonus: the route is still useful if they cannot be loaded.
        val cameras = suspendRunCatching { speedCameras.all() }.getOrDefault(emptyList())
        // Thousands of cameras × thousands of segments: keep it off the main thread.
        val onRoute = withContext(computationDispatcher) { cameras.alongTrack(route.track.map { it.point }) }
        return PlannedRoute(route, onRoute)
    }
}
